import Picker from "@/components/picker/mobile-picker";
import { PickerDivider } from "@/components/picker/mobile-picker/picker-divider";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useEffect, useMemo, useRef, useState } from "react";

interface SexSelectModalProps {
  defaultValue: string;
  options: { value: string; name: string }[];
  onSelect: (value: string) => void;
}

export const SexSelectModal = memo(function SexSelectModal({
  defaultValue,
  options,
  onSelect,
}: SexSelectModalProps) {
  const [tempValue, setTempValue] = useState<string | undefined>();

  const pickerValue = useMemo(
    () => ({
      sex: tempValue ?? defaultValue ?? options[0].value,
    }),
    [tempValue, defaultValue, options],
  );

  // Same fix as DateSelectModal — React synthetic onClick is dead inside
  // IonModal/IonNav; use a stable native click listener reading state via ref.
  const latestRef = useRef({ pickerValue, onSelect });
  latestRef.current = { pickerValue, onSelect };
  const confirmRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = confirmRef.current;
    if (!el) return;
    const handler = () => {
      const { pickerValue, onSelect } = latestRef.current;
      onSelect(pickerValue.sex as string);
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, []);

  return (
    <div className="space-y-3 p-9">
      <h3 className={textVariants.title3 + " text-center"}>Sex at birth</h3>
      <div className="relative">
        <PickerDivider itemHeight={64} />
        <Picker
          value={pickerValue}
          onChange={(val) => setTempValue(val.sex as string)}
          wheelMode="natural"
          itemHeight={64}
          className="mask-image-none"
          showDivider={false}
        >
          <Picker.Column name="sex">
            {options.map((option) => (
              <Picker.Item key={option.value} value={option.value}>
                {({ selected }) => (
                  <div
                    className={cn(
                      "flex h-16 items-center justify-center text-center !text-black opacity-50",
                      {
                        [textVariants.title1]: !selected,
                        [textVariants.largeTitle]: selected,
                        "w-full text-white opacity-100": selected,
                      },
                    )}
                  >
                    {option.name}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>
      </div>
      <div ref={confirmRef}>
        <FilledButton color="purple" text="Confirm Sex" />
      </div>
    </div>
  );
});
